import { compact, uniq } from 'lodash-es';
import type { ScheduledMeeting, UpdateMeetingParams } from '../../../contexts';
import { createMeetingAccessPayload } from '../../../utils/meetingAccessPayload';
import { deriveMeetingType } from '../../../utils/meetingType';
import type { PersonalMeetingData } from '../types';
import { LOBBY_MESSAGE_MAX_LENGTH } from './constants';
import { truncateLobbyMessage } from './sanitizers';

/**
 * Format API meeting to DTO
 */
export const formatMeetingToDto = (
  meeting: ScheduledMeeting,
  defaultMeetingId?: string | null,
): PersonalMeetingData => {
  return {
    meetingTitle: meeting.description || `Personal Meeting ${meeting.room}`,
    meetingId: meeting.room,
    passcode: meeting.password || '',
    lobbyMessage: truncateLobbyMessage(meeting.lobby_msg || '', LOBBY_MESSAGE_MAX_LENGTH),
    meetingType: deriveMeetingType(meeting),
    coHostEmails: meeting.moderators ?? [],
    isDefaultMeeting: meeting.room === defaultMeetingId,
  };
};

/**
 * Maps the local form state (DTO) to the API update payload (UpdateMeetingParams).
 */
export const mapStateToUpdatePayload = (
  meeting: ScheduledMeeting,
  formData: PersonalMeetingData,
  userEmail: string,
): UpdateMeetingParams => {
  const userDomain = userEmail?.split('@')[1] || '';
  const typePayload = createMeetingAccessPayload(formData.meetingType, userDomain);

  const payload: UpdateMeetingParams = {
    room: meeting.room,
    description: formData.meetingTitle,
    password: formData.passcode,
    lobby_msg: formData.lobbyMessage,
    moderators: formData.coHostEmails,
    ...typePayload,
  };

  if (meeting.room !== formData.meetingId) {
    payload.new_room = formData.meetingId;
  }

  return payload;
};

export const ensureTrimmedEmails = (emails: string[]): string[] => {
  return uniq(compact(emails.map((email) => email.trim().toLowerCase())));
};
